/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { cn } from "../../lib/utils";

/**
 * The Manatee wordmark. Inherits `currentColor`; size via className.
 * `variant="type"` drops the face glyph — for placements where the face
 * already appears on its own (e.g. next to an animated ManateeMark).
 */
export function Logo({
  variant = "full",
  className,
}: {
  variant?: "full" | "type";
  className?: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={variant === "full" ? "0 0 1800 299" : "505 25 1280 225"}
      role="img"
      aria-label="Manatee"
      className={cn("h-7 w-auto", className)}
    >
      <g fill="none" stroke="currentColor">
        {variant === "full" && (
          <>
            <g strokeWidth="18" strokeLinecap="butt" strokeLinejoin="round">
              <path d="M53 179 A144 144 0 0 1 341 179" />
              <path d="M197 178 V208" />
              <path d="M95 222 C111 244 132 251 150 251 C174 251 190 237 197 210" />
              <path d="M299 222 C283 244 262 251 244 251 C220 251 204 237 197 210" />
            </g>
            <g fill="currentColor" stroke="none">
              <circle cx="197" cy="210" r="3" />
              <circle cx="144" cy="140" r="13" />
              <circle cx="250" cy="140" r="13" />
            </g>
          </>
        )}
        <g fill="currentColor" stroke="none">
          <path
            d="M859 336V50Q859 28 845.0 13.5Q831 -1 808 -1Q786 -1 771.5 13.5Q757 28 757 50V336Q757 397 726.5 428.5Q696 460 645 460Q593 460 559.5 424.0Q526 388 526 332V50Q526 28 512.0 13.5Q498 -1 475 -1Q453 -1 438.5 13.5Q424 28 424 50V336Q424 397 393.5 428.5Q363 460 312 460Q259 460 225.5 424.0Q192 388 192 332V50Q192 28 178.0 13.5Q164 -1 141 -1Q119 -1 104.5 13.5Q90 28 90 50V495Q90 518 104.5 532.0Q119 546 141 546Q163 546 177.0 532.5Q191 519 192 497Q218 523 254.0 537.5Q290 552 332 552Q383 552 423.5 531.5Q464 511 490 473Q520 510 565.0 531.0Q610 552 665 552Q752 552 805.5 495.0Q859 438 859 336Z"
            transform="translate(503.000 228.692) scale(0.252 -0.252)"
          />
          <path
            d="M613 273V50Q613 28 598.5 13.5Q584 -1 562 -1Q540 -1 525.5 13.5Q511 28 511 50V87Q476 44 426.0 20.0Q376 -4 318 -4Q246 -4 187.5 32.0Q129 68 95.5 131.5Q62 195 62 273Q62 351 98.0 414.5Q134 478 197.5 514.5Q261 551 338 551Q415 551 477.5 514.5Q540 478 576.5 414.5Q613 351 613 273ZM515 273Q515 326 492.0 369.0Q469 412 428.5 436.5Q388 461 338 461Q288 461 247.5 436.5Q207 412 183.5 369.0Q160 326 160 273Q160 220 183.5 177.5Q207 135 247.5 110.5Q288 86 338 86Q388 86 428.5 110.5Q469 135 492.0 177.5Q515 220 515 273Z"
            transform="translate(749.207 228.692) scale(0.252 -0.252)"
          />
          <path
            d="M603 296V50Q603 28 588.5 13.5Q574 -1 552 -1Q530 -1 515.5 13.5Q501 28 501 50V296Q501 377 457.5 418.5Q414 460 342 460Q300 460 265.5 443.0Q231 426 211.5 397.0Q192 368 192 332V50Q192 28 178.0 13.5Q164 -1 141 -1Q119 -1 104.5 13.5Q90 28 90 50V496Q90 519 104.5 533.0Q119 547 141 547Q164 547 178.0 533.0Q192 519 192 496V485Q224 517 268.0 534.5Q312 552 362 552Q431 552 486.0 522.0Q541 492 572.0 434.5Q603 377 603 296Z"
            transform="translate(937.753 228.692) scale(0.252 -0.252)"
          />
          <path
            d="M613 273V50Q613 28 598.5 13.5Q584 -1 562 -1Q540 -1 525.5 13.5Q511 28 511 50V87Q476 44 426.0 20.0Q376 -4 318 -4Q246 -4 187.5 32.0Q129 68 95.5 131.5Q62 195 62 273Q62 351 98.0 414.5Q134 478 197.5 514.5Q261 551 338 551Q415 551 477.5 514.5Q540 478 576.5 414.5Q613 351 613 273ZM515 273Q515 326 492.0 369.0Q469 412 428.5 436.5Q388 461 338 461Q288 461 247.5 436.5Q207 412 183.5 369.0Q160 326 160 273Q160 220 183.5 177.5Q207 135 247.5 110.5Q288 86 338 86Q388 86 428.5 110.5Q469 135 492.0 177.5Q515 220 515 273Z"
            transform="translate(1127.834 228.692) scale(0.252 -0.252)"
          />
          <path
            d="M366 50Q366 28 349.5 14.0Q333 0 307 0H289Q240 0 199.5 25.5Q159 51 136.0 95.0Q113 139 113 194V441H67Q46 441 33.0 453.0Q20 465 20 483Q20 503 33.0 515.0Q46 527 67 527H113V671Q113 693 127.0 707.0Q141 721 163 721Q185 721 199.0 707.0Q213 693 213 671V527H298Q319 527 332.0 515.0Q345 503 345 483Q345 465 332.0 453.0Q319 441 298 441H213V194Q213 154 235.0 127.0Q257 100 289 100H323Q341 100 353.5 86.0Q366 72 366 50Z"
            transform="translate(1316.380 228.692) scale(0.252 -0.252)"
          />
          <path
            d="M535 238H155Q166 168 218.5 125.0Q271 82 347 82Q377 82 409.5 93.0Q442 104 463 120Q477 131 496 131Q515 131 526 121Q544 106 544 87Q544 69 528 57Q494 30 443.5 13.0Q393 -4 347 -4Q265 -4 200.0 31.5Q135 67 98.5 130.0Q62 193 62 273Q62 353 96.5 416.5Q131 480 192.5 515.5Q254 551 332 551Q409 551 465.0 517.0Q521 483 551.0 422.0Q581 361 581 282Q581 263 568.0 250.5Q555 238 535 238ZM156 318H489Q480 385 439.0 425.0Q398 465 332 465Q259 465 213.5 425.0Q168 385 156 318Z"
            transform="translate(1444.846 228.692) scale(0.252 -0.252)"
          />
          <path
            d="M535 238H155Q166 168 218.5 125.0Q271 82 347 82Q377 82 409.5 93.0Q442 104 463 120Q477 131 496 131Q515 131 526 121Q544 106 544 87Q544 69 528 57Q494 30 443.5 13.0Q393 -4 347 -4Q265 -4 200.0 31.5Q135 67 98.5 130.0Q62 193 62 273Q62 353 96.5 416.5Q131 480 192.5 515.5Q254 551 332 551Q409 551 465.0 517.0Q521 483 551.0 422.0Q581 361 581 282Q581 263 568.0 250.5Q555 238 535 238ZM156 318H489Q480 385 439.0 425.0Q398 465 332 465Q259 465 213.5 425.0Q168 385 156 318Z"
            transform="translate(1621.563 228.692) scale(0.252 -0.252)"
          />
        </g>
      </g>
    </svg>
  );
}
